import { useState } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, Loader2, Send, CheckCircle2, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export function EquityQuestions({ token, companyName, initial = [] }) {
  const [rows, setRows] = useState(initial);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) { toast.error("Write a fuller question (at least 10 characters)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/equity/${token}/questions`, { question: text.trim() });
      setRows((r) => [data.question, ...r]);
      setText("");
      toast.success(`Sent to ${companyName}. You'll get an email when they answer.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="rounded-xl border bg-card p-5" data-testid="equity-questions">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><MessageCircleQuestion className="h-4 w-4 text-primary" /> Ask {companyName} a question</h3>
      <p className="text-xs text-muted-foreground">Goes straight to the founder with your grant attached. Answers are the company's view, not tax advice.</p>
      <form onSubmit={submit} className="mt-3 space-y-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} placeholder="e.g. If I leave next year, how long do I have to exercise?" className="text-sm" data-testid="equity-question-input" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{text.length}/2000</span>
          <Button type="submit" size="sm" disabled={busy} className="gap-1.5" data-testid="equity-question-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send question</Button>
        </div>
      </form>
      {rows.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="equity-questions-list">
          {rows.map((q) => (
            <li key={q.id} className="rounded-lg border bg-secondary/40 p-3 text-sm" data-testid={`equity-question-${q.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-foreground">{q.question}</p>
                {q.status === "answered"
                  ? <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Answered</span>
                  : <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" /> Waiting</span>}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">Asked {formatDateTime(q.created_at)}</div>
              {q.answer && <div className="mt-2 whitespace-pre-wrap rounded-md border-l-2 border-primary bg-card p-2.5 text-sm text-foreground" data-testid={`equity-answer-${q.id}`}>{q.answer}</div>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
